import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  fetchAdminResultsApprovalList,
  bulkVerifyTournamentResults,
  bulkSetResultCertificateStatus,
  bulkSetResultCertificateTemplate,
  getTournamentLookups,
} from "../../../api/tournamentControlApi";
import { getCsrfToken } from "../../../../../public/api/authApi";
import { GlobalModal } from "../../../../../global/GlobalModal";
import GlobalErrorFlashModal from "../../../../../global/GlobalErrorFlashModal";
import GlobalSuccessFlashModal from "../../../../../global/GlobalSuccessFlashModal";
import { GlobalButton, GlobalSelectField } from "../../../../../global/forms/GlobalFormFields";
import GlobalBadge from "../../../../../global/GlobalBadge";
import {
  GlobalDirectoryShell,
  GlobalDirectoryFiltersSection,
  GlobalDirectoryGridSection,
  GlobalDirectoryPaginationSection,
  GlobalDirectoryColumnPicker,
  GlobalDirectoryExportMenu,
  GlobalDirectoryApprovalsSection,
} from "../../../../../global/directory";
import {
  RES_COL_KEY,
  RES_PAGE_SIZE,
  RESULT_COLUMN_DEFS,
  RESULT_PICKER_COLUMN_DEFS,
  loadVis,
  resCell,
  compareRes,
  isTruthyBool,
  formatDateTimeCell,
} from "../tournamentApprovalsCommon";
import "../../../styles/VolunteerTable.css";
import "../../../styles/participantsList.css";
import "../../../styles/tournamentAdminApprovals.css";

const COLUMN_DEFS_FOR_FILTERS = RESULT_COLUMN_DEFS.map((c) => ({
  id: c.id,
  label: c.label,
}));

const SORTABLE_IDS = new Set(RESULT_COLUMN_DEFS.filter((c) => c.sortable).map((c) => c.id));

const initialResFilters = () => ({
  student_search: "",
  column: "",
  value: "",
});

export default function TournamentResultApprovalsSection() {
  const [resRows, setResRows] = useState([]);
  const [loadingRes, setLoadingRes] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [resDraft, setResDraft] = useState(initialResFilters);
  const [resApplied, setResApplied] = useState(initialResFilters);
  const [resSortColumn, setResSortColumn] = useState("result_created_at");
  const [resSortOrder, setResSortOrder] = useState("DESC");
  const [resColVis, setResColVis] = useState(() => loadVis(RES_COL_KEY, RESULT_PICKER_COLUMN_DEFS));
  const [selectedRes, setSelectedRes] = useState(() => new Set());
  const resHeaderCb = useRef(null);
  const [busyRes, setBusyRes] = useState(false);
  const [detailRes, setDetailRes] = useState(null);
  const [resPage, setResPage] = useState(1);
  const [certificateTemplates, setCertificateTemplates] = useState([]);
  const [certTemplatePick, setCertTemplatePick] = useState("");
  const [certTogglePending, setCertTogglePending] = useState(null);
  const certToggleRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const look = await getTournamentLookups();
        setCertificateTemplates(look?.data?.certificateTemplates || []);
      } catch {
        /* ignore */
      }
    })();
  }, []);

  useEffect(() => {
    const t = setTimeout(() => setResApplied({ ...resDraft }), 400);
    return () => clearTimeout(t);
  }, [resDraft]);

  const loadResults = useCallback(async () => {
    setLoadingRes(true);
    setErrorMsg("");
    try {
      const res = await fetchAdminResultsApprovalList({
        student_search: (resApplied.student_search || "").trim() || undefined,
      });
      setResRows(res?.data || []);
      setSelectedRes(new Set());
    } catch (e) {
      setErrorMsg(e?.message || "Could not load tournament results.");
      setResRows([]);
    } finally {
      setLoadingRes(false);
    }
  }, [resApplied.student_search]);

  useEffect(() => {
    loadResults();
  }, [loadResults]);

  const resColumnFilterOptions = useMemo(
    () => RESULT_COLUMN_DEFS.map((c) => ({ value: c.id, label: c.label })),
    [],
  );

  const resValueFilterOptions = useMemo(() => {
    if (!resDraft.column) return [];
    const seen = new Set();
    const options = [];
    for (const row of resRows) {
      const value = resCell(resDraft.column, row);
      if (value == null || value === "—") continue;
      const label = String(value).trim();
      if (!label || seen.has(label)) continue;
      seen.add(label);
      options.push({ value: label, label });
    }
    return options.sort((a, b) =>
      a.label.localeCompare(b.label, undefined, { numeric: true, sensitivity: "base" }),
    );
  }, [resDraft.column, resRows]);

  const sortedRes = useMemo(() => {
    const col = RESULT_COLUMN_DEFS.find((c) => c.id === resSortColumn && c.sortable);
    if (!col) return resRows;
    const mult = resSortOrder === "ASC" ? 1 : -1;
    const list = [...resRows];
    list.sort((a, b) => mult * compareRes(a, b, resSortColumn));
    return list;
  }, [resRows, resSortColumn, resSortOrder]);

  const filteredRes = useMemo(() => {
    const col = resApplied.column;
    const value = String(resApplied.value || "").trim();
    if (!col || !value) return sortedRes;
    const match = value.toLowerCase();
    return sortedRes.filter((row) => String(resCell(col, row) ?? "").trim().toLowerCase() === match);
  }, [sortedRes, resApplied.column, resApplied.value]);

  const pagedRes = useMemo(() => {
    const start = (resPage - 1) * RES_PAGE_SIZE;
    return filteredRes.slice(start, start + RES_PAGE_SIZE);
  }, [filteredRes, resPage]);

  const resTotalPages = Math.max(1, Math.ceil(filteredRes.length / RES_PAGE_SIZE));
  const visibleResCols = useMemo(() => RESULT_COLUMN_DEFS.filter((c) => resColVis[c.id]), [resColVis]);

  useEffect(() => {
    if (resPage > resTotalPages) setResPage(resTotalPages);
  }, [resPage, resTotalPages]);
  useEffect(() => {
    setResPage(1);
  }, [resApplied]);

  const resPageIds = useMemo(() => pagedRes.map((r) => r.result_row_id), [pagedRes]);
  const allResSel = resPageIds.length > 0 && resPageIds.every((id) => selectedRes.has(id));
  const someResSel = resPageIds.some((id) => selectedRes.has(id));

  useEffect(() => {
    const el = resHeaderCb.current;
    if (el) el.indeterminate = !allResSel && someResSel;
  }, [allResSel, someResSel]);

  const csrf = async () => {
    let t = localStorage.getItem("csrf-token");
    if (!t) {
      t = await getCsrfToken();
      localStorage.setItem("csrf-token", t);
    }
    return t;
  };

  const certTemplateOpts = useMemo(
    () =>
      (certificateTemplates || []).map((t) => ({
        value: String(t.id),
        label: t.purpose_name || `Template ${t.id}`,
      })),
    [certificateTemplates],
  );

  const handleApplyCertificateTemplate = async () => {
    const ids = Array.from(selectedRes);
    if (ids.length === 0) {
      setErrorMsg("Select at least one result row.");
      return;
    }
    if (!certTemplatePick) {
      setErrorMsg("Choose a certificate template.");
      return;
    }
    setBusyRes(true);
    setErrorMsg("");
    try {
      await bulkSetResultCertificateTemplate(ids, Number(certTemplatePick), await csrf());
      setSuccessMsg("Certificate template applied.");
      await loadResults();
    } catch (e) {
      setErrorMsg(e?.message || "Certificate template update failed.");
    } finally {
      setBusyRes(false);
    }
  };

  const handleCertificateAllowedToggle = async (allowed) => {
    const ids = Array.from(selectedRes);
    if (ids.length === 0) {
      setErrorMsg("Select at least one result row.");
      return false;
    }
    if (!allSelectedVerified) {
      setErrorMsg("Approve results before changing certificate permission.");
      return false;
    }
    setBusyRes(true);
    setErrorMsg("");
    try {
      await bulkSetResultCertificateStatus(ids, allowed, await csrf());
      setSuccessMsg(allowed ? "Certificate generation allowed." : "Certificate generation disallowed.");
      await loadResults();
      return true;
    } catch (e) {
      setErrorMsg(e?.message || "Certificate status update failed.");
      return false;
    } finally {
      setBusyRes(false);
    }
  };

  const handleApproveResults = async () => {
    const ids = Array.from(selectedRes);
    if (ids.length === 0) {
      setErrorMsg("Select at least one result row.");
      return;
    }
    setBusyRes(true);
    setErrorMsg("");
    try {
      const res = await bulkVerifyTournamentResults(ids, await csrf());
      setSuccessMsg(res?.message || "Result(s) marked verified.");
      await loadResults();
    } catch (e) {
      setErrorMsg(e?.message || "Approve failed.");
    } finally {
      setBusyRes(false);
    }
  };

  const markOneResVerified = async (resultRowId) => {
    setBusyRes(true);
    setErrorMsg("");
    try {
      const res = await bulkVerifyTournamentResults([resultRowId], await csrf());
      setSuccessMsg(res?.message || "Result marked verified.");
      await loadResults();
    } catch (e) {
      setErrorMsg(e?.message || "Update failed.");
    } finally {
      setBusyRes(false);
    }
  };

  const toggleResSort = useCallback((columnId) => {
    const col = RESULT_COLUMN_DEFS.find((c) => c.id === columnId && c.sortable);
    if (!col) return;
    if (resSortColumn === columnId) setResSortOrder((o) => (o === "ASC" ? "DESC" : "ASC"));
    else {
      setResSortColumn(columnId);
      setResSortOrder("ASC");
    }
  }, [resSortColumn, resSortOrder]);

  const toggleAllRes = useCallback(() => {
    setSelectedRes((prev) => {
      const next = new Set(prev);
      if (allResSel) resPageIds.forEach((id) => next.delete(id));
      else resPageIds.forEach((id) => next.add(id));
      return next;
    });
  }, [allResSel, resPageIds]);

  const selectedCount = selectedRes.size;

  const findSelectedRow = useCallback(
    (id) => resRows.find((r) => Number(r.result_row_id) === Number(id)),
    [resRows],
  );

  const allSelectedVerified = useMemo(() => {
    if (selectedCount === 0) return false;
    for (const id of selectedRes) {
      const row = findSelectedRow(id);
      if (!row || !isTruthyBool(row.result_verified)) return false;
    }
    return true;
  }, [selectedCount, selectedRes, findSelectedRow]);

  const selectedCertStatus = useMemo(() => {
    if (selectedCount === 0) return null;
    let allowed = null;
    for (const id of selectedRes) {
      const row = findSelectedRow(id);
      if (!row) return null;
      const rowAllowed = isTruthyBool(row.certificate_status);
      if (allowed === null) allowed = rowAllowed;
      else if (allowed !== rowAllowed) return "mixed";
    }
    return allowed;
  }, [selectedCount, selectedRes, findSelectedRow]);

  const canSetCertificateStatus = selectedCount > 0 && allSelectedVerified && !busyRes;
  const certToggleChecked =
    certTogglePending !== null ? certTogglePending : selectedCertStatus === true;

  useEffect(() => {
    setCertTogglePending(null);
  }, [selectedRes]);

  useEffect(() => {
    const el = certToggleRef.current;
    if (el) el.indeterminate = selectedCertStatus === "mixed" && certTogglePending === null;
  }, [selectedCertStatus, certTogglePending]);

  const showResSelectCol = !!resColVis.__select;
  const showResActionsCol = !!resColVis.actions;

  const resTableHeaders = useMemo(
    () => [
      ...(showResSelectCol
        ? [
            {
              key: "__select",
              className: "checkbox-cell",
              label: (
                <input
                  ref={resHeaderCb}
                  type="checkbox"
                  aria-label="Select all"
                  checked={allResSel}
                  onChange={toggleAllRes}
                  disabled={pagedRes.length === 0}
                />
              ),
            },
          ]
        : []),
      ...visibleResCols.map((c) => {
        if (!SORTABLE_IDS.has(c.id)) {
          return { key: c.id, label: c.label };
        }
        return {
          key: c.id,
          label: (
            <button
              type="button"
              className="vpart-sort-btn"
              onClick={() => toggleResSort(c.id)}
            >
              {c.label}
              {resSortColumn === c.id ? (
                <span className="vpart-sort-ind" aria-hidden>
                  {resSortOrder === "ASC" ? "▲" : "▼"}
                </span>
              ) : null}
            </button>
          ),
        };
      }),
      ...(showResActionsCol
        ? [{ key: "actions", label: "Actions", className: "td-approvals-actions-col" }]
        : []),
    ],
    [
      showResSelectCol,
      showResActionsCol,
      visibleResCols,
      allResSel,
      pagedRes.length,
      resSortColumn,
      resSortOrder,
      toggleResSort,
      toggleAllRes,
    ],
  );

  const getResultExportData = useCallback(() => {
    const headers = visibleResCols.map((c) => c.label);
    const rows = filteredRes.map((row) =>
      visibleResCols.map((c) => String(resCell(c.id, row) ?? "")),
    );
    return { headers, rows };
  }, [visibleResCols, filteredRes]);

  return (
    <>
      <GlobalSuccessFlashModal message={successMsg} onDismiss={() => setSuccessMsg("")} />
      <GlobalErrorFlashModal message={errorMsg} onDismiss={() => setErrorMsg("")} />

      <div
        id="td-approvals-results"
        className="user-builder-panel"
        style={{ scrollMarginTop: "2rem", marginTop: "1.5rem" }}
      >
        <div className="user-builder-panel__head">
          <h3 className="user-builder-panel__title">Result approvals</h3>
        </div>

        <GlobalDirectoryShell
          filters={
            <GlobalDirectoryFiltersSection
              className="ub-filters ub-directory-filters td-approvals-hscroll"
              searchLabel="Search student"
              searchPlaceholder="Name, code, or phone…"
              searchValue={resDraft.student_search}
              onSearchChange={(v) => setResDraft((f) => ({ ...f, student_search: v }))}
              columnLabel="Column"
              columnValue={resDraft.column}
              onColumnChange={(v) => setResDraft((f) => ({ ...f, column: v, value: "" }))}
              gridColumnDefs={COLUMN_DEFS_FOR_FILTERS}
              valueLabel="Value"
              valueValue={resDraft.value}
              onValueChange={(v) => setResDraft((f) => ({ ...f, value: v }))}
              valueOptions={resValueFilterOptions}
              onClear={() => setResDraft(initialResFilters())}
              clearFilterLabel="Clear filter"
              secondaryStart={
                <GlobalDirectoryColumnPicker
                  columnDefs={RESULT_PICKER_COLUMN_DEFS}
                  visibility={resColVis}
                  onVisibilityChange={setResColVis}
                  storageKey={RES_COL_KEY}
                />
              }
              secondaryEnd={
                <GlobalDirectoryExportMenu
                  getExportData={getResultExportData}
                  filenameBase={`admin-tournament-result-approvals_${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, "0")}-${String(new Date().getDate()).padStart(2, "0")}`}
                />
              }
            />
          }
          approvals={
            <GlobalDirectoryApprovalsSection
              className="gds-approvals--result-approvals-row"
              actions={
                <div className="td-res-approvals-toolbar" aria-label="Bulk actions for selected result rows">
                  <div className="td-res-approvals-toolbar__start">
                    <span
                      className={`td-res-approvals-toolbar__count${
                        selectedCount === 0 ? " td-res-approvals-toolbar__count--idle" : ""
                      }`}
                      aria-live="polite"
                      title={selectedCount === 0 ? "Select rows in the table" : `${selectedCount} row(s) selected`}
                    >
                      {selectedCount > 0 ? selectedCount : "—"}
                    </span>

                    <div className="td-res-approvals-toolbar__cluster">
                      <span className="td-res-approvals-toolbar__tag">Template</span>
                      <GlobalSelectField
                        id="td-res-cert-template"
                        label="Certificate template"
                        value={certTemplatePick}
                        onChange={setCertTemplatePick}
                        placeholderOption="Choose…"
                        options={certTemplateOpts}
                        disabled={busyRes || selectedCount === 0}
                        classNames={{
                          label: "td-res-approvals-toolbar__sr",
                          fieldWrap: "td-res-approvals-toolbar__select",
                        }}
                      />
                      <GlobalButton
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={handleApplyCertificateTemplate}
                        loading={busyRes}
                        disabled={selectedCount === 0}
                        title="Apply certificate template to selected rows"
                      >
                        Apply
                      </GlobalButton>
                    </div>

                    <span className="td-res-approvals-toolbar__sep" aria-hidden />

                    <div
                      className="td-res-approvals-toolbar__cluster td-res-approvals-toolbar__cluster--cert"
                      title={
                        selectedCount > 0 && !allSelectedVerified
                          ? "Approve results before changing certificate permission"
                          : undefined
                      }
                    >
                      <span className="td-res-approvals-toolbar__tag">Certificate</span>
                      <span className="td-res-approvals-toolbar__cert-off">Off</span>
                      <label className="td-res-approvals-switch" htmlFor="td-res-cert-allowed-toggle">
                        <input
                          ref={certToggleRef}
                          id="td-res-cert-allowed-toggle"
                          type="checkbox"
                          role="switch"
                          aria-label="Allow certificate for selected rows"
                          checked={certToggleChecked}
                          disabled={!canSetCertificateStatus}
                          onChange={(e) => {
                            const next = e.target.checked;
                            setCertTogglePending(next);
                            void handleCertificateAllowedToggle(next).then((ok) => {
                              if (!ok) setCertTogglePending(null);
                            });
                          }}
                        />
                        <span className="td-res-approvals-switch__track" aria-hidden />
                      </label>
                      <span className="td-res-approvals-toolbar__cert-on">On</span>
                    </div>
                  </div>

                  <div className="td-res-approvals-toolbar__end">
                    <GlobalButton
                      type="button"
                      variant="primary"
                      size="sm"
                      onClick={handleApproveResults}
                      loading={busyRes}
                      disabled={selectedCount === 0}
                      title="Mark selected results as verified"
                    >
                      Approve results
                    </GlobalButton>
                  </div>
                </div>
              }
            />
          }
          grid={
            <GlobalDirectoryGridSection
              loading={loadingRes}
              loadingMessage="Loading results…"
              headers={resTableHeaders}
              isEmpty={!loadingRes && pagedRes.length === 0}
              emptyMessage="No result rows match your filters."
              tableAriaLabel="Tournament results approval"
              wrapClassName="vt-table-wrap td-approvals-table-scroll"
              tableClassName="vt-table"
            >
              {pagedRes.map((row) => (
                <tr key={row.result_row_id}>
                  {showResSelectCol ? (
                    <td className="checkbox-cell" style={{ textAlign: "center" }}>
                      <input
                        type="checkbox"
                        checked={selectedRes.has(row.result_row_id)}
                        onChange={() =>
                          setSelectedRes((prev) => {
                            const next = new Set(prev);
                            if (next.has(row.result_row_id)) next.delete(row.result_row_id);
                            else next.add(row.result_row_id);
                            return next;
                          })
                        }
                        aria-label={`Select ${row.student_name}`}
                      />
                    </td>
                  ) : null}
                  {visibleResCols.map((col) => (
                    <td key={col.id}>
                      {col.id === "result_verified" ? (
                        <GlobalBadge
                          active={isTruthyBool(row.result_verified)}
                          activeText="Verified"
                          inactiveText="Not verified"
                        />
                      ) : col.id === "certificate_status" ? (
                        <GlobalBadge
                          active={isTruthyBool(row.certificate_status)}
                          activeText="Allowed"
                          inactiveText="Not allowed"
                        />
                      ) : col.id === "student_name" ? (
                        <span style={{ fontWeight: 600 }}>{resCell(col.id, row)}</span>
                      ) : (
                        resCell(col.id, row)
                      )}
                    </td>
                  ))}
                  {showResActionsCol ? (
                    <td className="td-approvals-actions">
                      <div className="td-approvals-actions-inner">
                        <GlobalButton type="button" variant="ghost" size="sm" onClick={() => setDetailRes(row)}>
                          View
                        </GlobalButton>
                        <GlobalButton
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => markOneResVerified(row.result_row_id)}
                          disabled={busyRes}
                        >
                          Mark result verified
                        </GlobalButton>
                      </div>
                    </td>
                  ) : null}
                </tr>
              ))}
            </GlobalDirectoryGridSection>
          }
          pagination={
            <GlobalDirectoryPaginationSection
              page={resPage}
              totalPages={resTotalPages}
              total={filteredRes.length}
              pageSize={RES_PAGE_SIZE}
              onPageChange={setResPage}
              label="Tournament result approvals pagination"
            />
          }
        />
      </div>

      <GlobalModal
        open={detailRes != null}
        onClose={() => setDetailRes(null)}
        title="Tournament result"
        description={
          detailRes ? `${detailRes.student_name} — ${detailRes.tournament_name}` : undefined
        }
        size="md"
        classNames={{
          modal: "gff-modal vpart-reg-modal",
          header: "gff-modal__header vpart-reg-modal__header",
          title: "gff-modal__title vpart-reg-modal__title",
          description: "gff-modal__description vpart-reg-modal__subtitle",
          body: "gff-modal__body vpart-reg-modal__body",
        }}
      >
        {detailRes ? (
          <div className="vpart-reg-modal__scroll">
            <article className="vpart-reg-card">
              <h3 className="vpart-reg-card__title">Details</h3>
              <div className="vpart-reg-fields">
                <div className="vpart-reg-field">
                  <span className="vpart-reg-field__label">Result type</span>
                  <span className="vpart-reg-field__value">{detailRes.result_name ?? "—"}</span>
                </div>
                <div className="vpart-reg-field">
                  <span className="vpart-reg-field__label">Certificate template</span>
                  <span className="vpart-reg-field__value">{detailRes.certificate_template_name ?? "—"}</span>
                </div>
                <div className="vpart-reg-field">
                  <span className="vpart-reg-field__label">Certificate allowed</span>
                  <span className="vpart-reg-field__value">
                    {isTruthyBool(detailRes.certificate_status) ? "Yes" : "No"}
                  </span>
                </div>
                <div className="vpart-reg-field">
                  <span className="vpart-reg-field__label">Result verified</span>
                  <span className="vpart-reg-field__value">
                    {isTruthyBool(detailRes.result_verified) ? "Yes" : "No"}
                  </span>
                </div>
                <div className="vpart-reg-field">
                  <span className="vpart-reg-field__label">Recorded on</span>
                  <span className="vpart-reg-field__value">
                    {formatDateTimeCell(detailRes.result_created_at)}
                  </span>
                </div>
              </div>
            </article>
          </div>
        ) : null}
      </GlobalModal>
    </>
  );
}
